/**
 * artFiles: finds the painted pieces that exist, at build time.
 *
 * Vite expands the glob below into a map of whatever WebP files are in
 * src/assets/art/ when the app is built. A picture that is not there is simply
 * not in the map, so the page falls back without ever asking the network for
 * it. That is why this is a glob and not an <img> with an onError handler: a
 * missing picture costs nothing and logs nothing.
 *
 * Files are named `<stem>-<width>.webp` by scripts/art/ingest-art.mjs, for
 * example portrait-philemon-256.webp. The stems are listed in data/artwork.js.
 */
const urls = import.meta.glob('../../assets/art/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
});

const byStem = {};
for (const [file, url] of Object.entries(urls)) {
  const match = file.match(/\/([a-z]+-[a-z0-9]+)-(\d+)\.webp$/);
  if (!match) continue;
  (byStem[match[1]] ||= []).push({ width: Number(match[2]), url });
}
for (const sizes of Object.values(byStem)) sizes.sort((a, b) => a.width - b.width);

/**
 * The sizes of one piece, or null if it has not been made yet.
 * `src` is the middle size, for browsers that ignore srcSet.
 */
export const getArt = (stem) => {
  const sizes = byStem[stem];
  if (!sizes?.length) return null;
  return {
    src: sizes[Math.floor((sizes.length - 1) / 2)].url,
    srcSet: sizes.map((size) => `${size.url} ${size.width}w`).join(', '),
  };
};

/** True once at least one painted piece is in the build. Gates the credit line. */
export const hasPaintedArt = Object.keys(byStem).length > 0;
